import { PASSWORD, USERNAME } from "./consts";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useLoginMutation } from "@/services";
import { toast } from "@/components/Toast";
import { setCookie } from "@/lib/setCookie";
import { routes } from "@/routes/utils";
import {
  getRequiredFields,
  removeEmptyFields,
  ROLE_CAM,
  ROLE_CAM_ACCOUNTANT,
  ROLE_OWNER,
} from "@/lib/utils";
import {
  setCurrentRole,
  setLoggedInUserData,
  setPropertyData,
} from "@/redux/slices";
import { useDispatch } from "react-redux";
import { schema } from "./validations";

const useLogin = () => {
  const navigate = useNavigate();
  const [isPassword, setIsPassword] = useState<boolean>(true);
  const dispatch = useDispatch();

  const form = useForm<any>({
    defaultValues: {
      [USERNAME]: "",
      [PASSWORD]: "",
    },
    resolver: zodResolver(schema),
  });

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  const { mutate: loginMutate, isPending: isLoginPending } = useLoginMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
      return error?.response?.data?.message;
    },
    onSuccess: (data) => {
      if (data?.data?.token) {
        if (data.data.roles?.length == 0) {
          toast.error(
            "No roles are assigned to you. Please reach out to the system admin."
          );
        } else {
          dispatch(setLoggedInUserData(data.data));
          setCookie("accessToken", data?.data?.token);
          if (data.data.roles?.includes(ROLE_OWNER)) {
            dispatch(setCurrentRole(ROLE_OWNER));
            dispatch(setPropertyData(null));
            if (data.data.forcePasswordChange) {
              navigate(`/${routes.changePassword}`);
            } else {
              navigate(`/${routes.owner}`);
            }
          } else if (
            data.data.roles?.includes(ROLE_CAM) ||
            data.data.roles?.includes(ROLE_CAM_ACCOUNTANT)
          ) {
            if (data.data.forcePasswordChange) {
              navigate(`/${routes.changePassword}`);
            } else {
              navigate(`/${routes.cam}`);
            }
          } else {
            navigate(`/${routes.admin}`);
          }
        }
      }
    },
  });

  const onSubmit = (data: any) => {
    const _data = removeEmptyFields(data);
    loginMutate({ data: _data });
  };

  const onEyeIconClick = () => {
    setIsPassword(!isPassword);
  };

  const Icon = isPassword ? Eye : EyeOff;

  const onForgotClick = () => {
    navigate(`/${routes.forgotPassword}`);
  };
  
  return {
    navigate,
    form,
    requiredFields,
    onSubmit,
    onEyeIconClick,
    isPassword,
    Icon,
    isLoginPending,
    onForgotClick,
  };
};

export default useLogin;
